import React from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Avatar, 
  Chip,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DriveEta as CarIcon,
  Person as PersonIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
} from '@mui/icons-material';
import { formatDate, getStatusColor, formatStatusLabel } from '../utils/helpers';

const DriverCard = ({ 
  driver, 
  onClick, 
  onApprove, 
  onReject, 
  onEdit, 
  onDelete,
  onAssignVehicle,
  onToggleLock,
}) => {
  // Get initials from name
  const getInitials = (name) => {
    if (!name) return 'D';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const handleCardClick = (e) => {
    // Prevent card click when clicking action buttons
    if (e.target.closest('.action-button')) return;
    onClick && onClick(driver);
  };

  const statusLabel = driver.isLocked
    ? 'Locked'
    : driver.approved
      ? 'Approved'
      : formatStatusLabel(driver.status, 'Pending');

  const statusColor = driver.isLocked
    ? 'error'
    : getStatusColor(driver.approved ? 'approved' : driver.status);

  return (
    <Card 
      sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s',
        border: '1px solid #e0e0e0',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4,
          borderColor: '#DD2424'
        }
      }}
    >
      <CardContent sx={{ flexGrow: 1, cursor: 'pointer' }} onClick={handleCardClick}>
        {/* Avatar and Name */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar 
            src={driver.profilePictureUrl || ''}
            sx={{ 
              bgcolor: '#f5f5f5', 
              width: 60, 
              height: 60,
              fontSize: '1.5rem',
              fontWeight: 'bold',
              mr: 2,
              border: '2px solid #e0e0e0'
            }}
          >
            {getInitials(driver.name)}
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
              {driver.name || 'N/A'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {driver.driverId || 'No ID'}
            </Typography>
            <Chip
              label={statusLabel}
              color={statusColor}
              size="small"
              sx={{ mt: 0.5 }}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Driver Details */}
        <Box sx={{ mb: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            📧 {driver.email || 'No email'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            📱 {driver.phone || 'No phone'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            🪪 License: {driver.licenseNumber || 'Not provided'}
          </Typography>
          {driver.isLocked && (
            <Typography
              variant="body2"
              sx={{
                color: '#d32f2f',
                fontWeight: 600,
                mt: 0.5,
              }}
            >
              Access locked: {driver.lockReason || 'Contact admin'}
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Vehicle Information */}
        <Box sx={{ mb: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            Vehicle Information:
          </Typography>
          
          {/* Own Vehicle */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mb: 1,
            p: 1,
            bgcolor: driver.ownVehicle ? '#e8f5e9' : '#f5f5f5',
            borderRadius: 1
          }}>
            <CarIcon sx={{ mr: 1, color: driver.ownVehicle ? '#4caf50' : '#999' }} />
            <Box>
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                Own Vehicle: {driver.ownVehicle ? driver.ownVehicle.type : 'None'}
              </Typography>
              {driver.ownVehicle && (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {driver.ownVehicle.make} {driver.ownVehicle.model} ({driver.ownVehicle.year})
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Seats: {driver.ownVehicle.seats} | {driver.ownVehicle.plateNumber}
                  </Typography>
                </>
              )}
            </Box>
          </Box>

          {/* Assigned Vehicle */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            p: 1,
            bgcolor: driver.assignedVehicle ? '#fff3e0' : '#f5f5f5',
            borderRadius: 1
          }}>
            <PersonIcon sx={{ mr: 1, color: driver.assignedVehicle ? '#ff9800' : '#999' }} />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                Assigned Vehicle: {driver.assignedVehicle ? driver.assignedVehicle.type : 'Not assigned'}
              </Typography>
              {driver.assignedVehicle && (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {driver.assignedVehicle.make} {driver.assignedVehicle.model}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {driver.assignedVehicle.plateNumber}
                  </Typography>
                </>
              )}
            </Box>
            {onAssignVehicle && (
              <Tooltip title="Assign Vehicle">
                <IconButton 
                  size="small" 
                  className="action-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onAssignVehicle(driver);
                  }}
                  sx={{ 
                    color: '#DD2424',
                    '&:hover': { bgcolor: 'rgba(221, 36, 36, 0.1)' }
                  }}
                >
                  <CarIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Additional Info */}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Available: {(() => {
            // Check new availabilitySchedule system
            if (driver.availabilitySchedule && Array.isArray(driver.availabilitySchedule) && driver.availabilitySchedule.length > 0) {
              const now = new Date();
              const activeSchedules = driver.availabilitySchedule.filter(avail => {
                if (!avail.isAvailable) return false;
                const endDate = avail.endDate?.toDate ? avail.endDate.toDate() : new Date(avail.endDate);
                return endDate > now;
              });
              if (activeSchedules.length > 0) {
                // Show the earliest end date
                const earliestEnd = activeSchedules.reduce((earliest, current) => {
                  const currentEnd = current.endDate?.toDate ? current.endDate.toDate() : new Date(current.endDate);
                  const earliestEnd = earliest.endDate?.toDate ? earliest.endDate.toDate() : new Date(earliest.endDate);
                  return currentEnd < earliestEnd ? current : earliest;
                });
                const endDate = earliestEnd.endDate?.toDate ? earliestEnd.endDate.toDate() : new Date(earliestEnd.endDate);
                return formatDate(endDate);
              }
            }
            // Fallback to old availableUntil field
            return driver.availableUntil ? formatDate(driver.availableUntil) : 'Not set';
          })()}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Joined: {formatDate(driver.createdAt)}
        </Typography>
      </CardContent>

      {/* Action Buttons */}
      <Box sx={{ 
        p: 1.5, 
        pt: 1,
        bgcolor: '#fafafa', 
        borderTop: '1px solid #e0e0e0',
        display: 'flex',
        justifyContent: 'space-around'
      }}>
        {!driver.approved && driver.status !== 'rejected' && onApprove && (
          <Tooltip title="Approve">
            <IconButton
              size="small"
              className="action-button"
              onClick={(e) => {
                e.stopPropagation();
                onApprove(driver.id);
              }}
              sx={{ color: '#4caf50' }}
            >
              <ApproveIcon />
            </IconButton>
          </Tooltip>
        )}
        {!driver.approved && driver.status !== 'rejected' && onReject && (
          <Tooltip title="Reject">
            <IconButton
              size="small"
              className="action-button"
              onClick={(e) => {
                e.stopPropagation();
                onReject(driver.id);
              }}
              sx={{ color: '#f44336' }}
            >
              <RejectIcon />
            </IconButton>
          </Tooltip>
        )}
        {onEdit && (
          <Tooltip title="Edit">
            <IconButton
              size="small"
              className="action-button"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(driver);
              }}
              sx={{ color: '#2196f3' }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
        )}
        {onDelete && (
          <Tooltip title="Delete">
            <IconButton
              size="small"
              className="action-button"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(driver.id);
              }}
              sx={{ color: '#f44336' }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        )}
        {onToggleLock && (
          <Tooltip title={driver.isLocked ? 'Unlock Access' : 'Lock Access'}>
            <IconButton
              size="small"
              className="action-button"
              onClick={(e) => {
                e.stopPropagation();
                onToggleLock(driver);
              }}
              sx={{ color: driver.isLocked ? '#2e7d32' : '#ed6c02' }}
            >
              {driver.isLocked ? <LockOpenIcon /> : <LockIcon />}
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Card>
  );
};

export default DriverCard;
